import { useAdminAccess } from "../hooks/useAdminAccess";
import { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { SudoButton } from "src/sudokit/components/SudoButton";
import Auth from "../Auth";
import { useToast } from "../components/ToastWrapper";

function SharedDocuments(props) {
  const { can } = useAdminAccess();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const { setToast } = useToast();
  async function fetchSharedDocuments() {
    setLoading(true);
    try {
      const response = await Auth.fetchWithIdToken(`/api/admin/user/${props.user.uid}/shared-documents`);
      setDocuments(response.documents || []);
    } catch (error) {
      setToast(`Error fetching shared documents: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (props.user) {
      fetchSharedDocuments();
    }
  }, [props.user, refreshTrigger]);

  const handleUnpublish = async (documentId, isRevoked) => {
    try {
      await Auth.fetchWithIdToken(`/api/admin/shared-documents/${documentId}/${isRevoked ? "restore" : "unpublish"}`, {
        method: "POST",
      });

      // Update the local state without refreshing the page
      setDocuments(
        documents.map((doc) => {
          if (doc.documentId === documentId) {
            return {
              ...doc,
              revoked: !isRevoked,
              revokedAt: !isRevoked ? new Date() : null,
            };
          }
          return doc;
        })
      );
    } catch (error) {
      setToast(`Error ${isRevoked ? "restoring" : "unpublishing"} document: ${error.message}`);
    }
  };

  const columns = [
    {
      name: "ID",
      width: "100px",
      selector: (row) => row.shortId,
      sortable: false,
    },
    ...(can("documents.readContent") ? [
    {
      name: "Title",
      width: "300px",
      selector: (row) => row.title || "Untitled",
      sortable: false,
    },
    {
      name: "Link",
      cell: (row) => {
        const link = `${window.location.origin}/app/documents/${row.shortId}/feedback`;
        return (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-app-primary-cta hover:underline px-1 py-1 rounded-md overflow-hidden text-ellipsis whitespace-nowrap"
          >
            {link}
          </a>
        );
      },
      sortable: false,
    },
    ] : []),
    {
      name: "Published At",
      width: "200px",
      selector: (row) => new Date(row.publishedAt).toLocaleString(),
      sortable: false,
    },
    {
      name: "Status",
      width: "150px",
      cell: (row) => {
        if (row.revokedAt) {
          const revokedDate = row.revokedAt ? new Date(row.revokedAt).toLocaleString() : "unknown date";
          return <span>Revoked on {revokedDate}</span>;
        }
        return <span style={{ color: "#4CAF50" }}>Live</span>;
      },
      sortable: false,
    },
    {
      name: "Actions",
      width: "150px",
      cell: (row) => (
        <SudoButton
          onClick={() => handleUnpublish(row.documentId, row.revokedAt)}
          variant={row.revokedAt ? "secondary" : "danger"}
          size="small"
        >
          {row.revokedAt ? "Restore" : "Unpublish"}
        </SudoButton>
      ),
      sortable: false,
    },
  ];

  const customStyles = {
    rows: {
      style: {
        minHeight: "60px", // Override the row height for better vertical alignment
      },
    },
  };

  return (
    <>
      <DataTable
        columns={columns}
        responsive={true}
        progressPending={loading}
        pagination
        paginationPerPage={25}
        paginationRowsPerPageOptions={[25]}
        noDataComponent="No shared documents found"
        data={documents}
        customStyles={customStyles}
      />
    </>
  );
}

export default SharedDocuments;
